<template lang="pug">
Card.cards.form.selections-card
  template(#content)
    .intro
      .title-row
        h1.title Explore your home upgrade roadmap
        div(v-if="formComplete")
          Button.p-button-text.header-download-link(
            label="Download home upgrade roadmap"
            icon="pi pi-download"
            @click="downloadPassport"
          )

      .text(v-if="!passportStarted")
        | The home upgrade roadmap is a tool that helps you design a customised step-by-step renovation plan for your home.
        | It allows you to explore, organise, and schedule energy efficiency measures across multiple years - tailored to your building's unique characteristics and your goals.

      .start-actions(v-if="!passportStarted")
        Button.generate-button.submit-button(
          label="Generate my roadmap"
          icon="pi pi-home"
          :loading="loading"
          @click="startPassport"
        )

    template(v-if="passportStarted")
      .passport-copy
        | Your home upgrade roadmap below sets out the recommended steps to carry out a deep renovation of your building following the energy efficiency first principle.

      BrStrategySelector(
        v-if="selectedStrategyForUi"
        :selectedStrategy="selectedStrategyForUi"
        :strategies="strategies"
        :currentStrategyId="selectedStrategy?.id"
        :isReadOnly="isReadOnly"
        :goalPickerVisible="goalPickerVisible"
        @toggle-picker="goalPickerVisible = !goalPickerVisible"
        @select-strategy="onStrategyChange"
      )

      BrUpgradedMeasures(
        :measures="alreadyUpgraded"
        :selectedStrategy="selectedStrategyForUi"
        :isReadOnly="isReadOnly"
        :upgradedYears="upgradedYears"
        :upgradedYearOptions="upgradedYearOptions"
        @update-year="onYearChange"
      )

      BrTimeline(
        :steps="timelineSteps"
        :canAddStep="canAddTimelineStep"
        :isReadOnly="isReadOnly"
        :beforeRatingCode="report?.beforeRating.code"
        :afterRatingCode="report?.afterRating.code"
        :canMoveMeasure="canMoveTimelineMeasure"
        :canMoveStep="canMoveTimelineStep"
        @update-step-year="setStepYear"
        @move-step="onMoveStep"
        @move-measure="onMoveMeasure"
        @add-step="onAddStep"
        @remove-step="onRemoveStep"
      )

      .actions
        Button.p-button-outlined(
          label="Reset plan"
          icon="pi pi-refresh"
          :disabled="isGeneratingReport"
          @click="showPopup"
        )
        Button.submit-button(
          :label="formComplete ? 'Download home upgrade roadmap' : 'Generate home upgrade roadmap'"
          :disabled="(!formComplete && !canSubmit) || isGeneratingReport"
          :loading="isGeneratingReport"
          icon="pi pi-download"
          @click="formComplete ? downloadPassport() : submitPassport()"
        )
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useConfirm } from "primevue/useconfirm";

import Button from "primevue/button";
import Card from "primevue/card";

import BrStrategySelector from "@/components/result/br-passport/BrStrategySelector.vue";
import BrTimeline from "@/components/result/br-passport/BrTimeline.vue";
import BrUpgradedMeasures from "@/components/result/br-passport/BrUpgradedMeasures.vue";
import { defaultBrpRoadmapConfig, mergeBrpRoadmapConfig } from "@/config/brp-roadmap";
import { usePassport, useRecommendations, useSettings } from "@/store";
import { OcrApiService } from "@/service/ocr-api.service";
import {
  buildPlanFromStrategy,
  canMovePlanMeasure,
  canMovePlanStep,
  filterRoadmapStrategies,
  getAlreadyUpgradedRecommendations,
  populateRoadmapOrdersWithRecommendationGroups,
  type BrpEcmKey,
  type BrpRouteKey,
  type PlanYearStep,
  type RoadmapStrategy
} from "@/helpers";
import type { RecommendationGroup } from "@/types/recommendations";
import { useSelections } from "@/store/selections";

type UpgradedYearOption = {
  label: string
  value: number
};

type TimelineMeasureViewModel = {
  key: string
  name: string
  description: string
  ecmKey?: BrpEcmKey | null
  routeKey?: BrpRouteKey | null
  comfort?: number
  cost?: string | number
  icon?: string | null
  defaultIcon?: string | null
  recommendationNames: string[]
};

type TimelineStepViewModel = {
  key: string
  index: number
  displayedYear?: number
  yearOptions: number[]
  durationLabel: string | null
  leavesFollowingStepsInvalid: boolean
  hasNoValidYearOptions: boolean
  canRemoveEmptyStep: boolean
  measures: TimelineMeasureViewModel[]
};

type TimelineMeasureMovePayload = {
  sourceStepIndex: number
  sourceMeasureIndex: number
  targetStepIndex: number
  targetMeasureIndex?: number
};

type TimelineStepMovePayload = {
  sourceStepIndex: number
  targetStepIndex: number
};

type PlanMeasureWithOptionalDuration = PlanYearStep["measures"][number] & {
  duration?: number | null
};

const passportStore = usePassport();
const recommendationsStore = useRecommendations();
const settingsStore = useSettings();
const selectionsStore = useSelections();
const { setSelectedGoal, clear } = passportStore;

const {
  formComplete,
  isGeneratingReport,
  upgradedYears,
  selectedGoal,
  selectedStrategy,
  planYearSteps,
  roadmap
} = storeToRefs(passportStore);
const { report } = storeToRefs(recommendationsStore);
const { settings } = storeToRefs(settingsStore);
const {
  age,
  berData,
  epcData,
  houseTypologySelected,
  mprnDetails
} = storeToRefs(selectionsStore);

const confirm = useConfirm();

const loading = ref(false);
const passportStarted = ref(false);
const goalPickerVisible = ref(false);
const startYear = ref(new Date().getFullYear());

const maxAllowedYear = 2035;
const defaultStrategyId = "IE.maxComfort11";
const earliestUpgradeYear = 1900;

const recommendationGroups = computed(() => recommendationsStore.recommendationGroups ?? []);
const roadmapConfig = computed(() =>
    mergeBrpRoadmapConfig(
        defaultBrpRoadmapConfig,
        settings.value?.brpRoadmapOptions
    )
);
const roadmapStrategyAvailabilityContext = computed(() => {
  const typology =
      houseTypologySelected.value ??
      berData.value?.typology ??
      epcData.value?.typology ??
      null;

  return {
    ageBand: typology?.ageBand ?? null,
    selectedAge: age.value,
    typologyCode: typology?.code ?? null,
    yearOfConstruction:
        berData.value?.berDetails?.yearOfConstruction ??
        mprnDetails.value?.yearOfConstruction ??
        null
  };
});
const strategies = computed(() =>
    filterRoadmapStrategies(
        roadmap.value,
        roadmapStrategyAvailabilityContext.value,
        roadmapConfig.value
    )
);

const selectedStrategyForUi = computed(() =>
    strategies.value.find((strategy) => strategy.id === selectedStrategy.value?.id)
);

const strategyDurationByMeasureKey = computed(() => {
  const strategy = selectedStrategyForUi.value;
  if (!strategy) return new Map<string, number | null>();

  return new Map<string, number | null>(
      strategy.strategy_orders.map((order) => [
        `${strategy.id}:${order.id}`,
        order.duration ?? null
      ])
  );
});

const alreadyUpgraded = computed(() =>
    getAlreadyUpgradedRecommendations(recommendationGroups.value)
);

const isReadOnly = computed(() => formComplete.value || isGeneratingReport.value);

const yearOptions = computed(() => {
  const from = startYear.value;
  const to = maxAllowedYear;
  return Array.from({ length: Math.max(0, to - from + 1) }, (_, index) => from + index);
});

const upgradedYearOptions = computed<UpgradedYearOption[]>(() => {
  const currentYear = new Date().getFullYear();
  return Array.from(
      { length: Math.max(0, currentYear - earliestUpgradeYear + 1) },
      (_, index) => ({
        label: String(currentYear - index),
        value: currentYear - index
      })
  );
});

const canAddTimelineStep = computed(() => passportStore.canAddPlanStep());

function getMappedDurationMonths(measureKey: string) {
  const duration = strategyDurationByMeasureKey.value.get(measureKey);
  return typeof duration === "number" ? duration : null;
}

function getMeasureDurationMonths(measure: PlanMeasureWithOptionalDuration) {
  if (typeof measure.durationMonths === "number") return measure.durationMonths;
  if (typeof measure.duration === "number") return measure.duration;
  return getMappedDurationMonths(measure._key);
}

function collectStepDurationMonths(step: PlanYearStep) {
  const durationCandidates = step.measures
      .filter((measure) => measure.included !== false)
      .map((measure) => getMeasureDurationMonths(measure))
      .filter((duration): duration is number => duration != null);

  return durationCandidates.length ? Math.max(...durationCandidates) : null;
}

const stepDurationMonthsByIndex = computed(() =>
    planYearSteps.value.map((step) => collectStepDurationMonths(step))
);

function getStepDurationMonths(index: number) {
  return stepDurationMonthsByIndex.value[index] ?? null;
}

function getStepDurationYears(index: number): number {
  const maxMonths = getStepDurationMonths(index) ?? 12;
  return Math.max(1, Math.ceil(maxMonths / 12));
}

function getStepDurationLabel(index: number) {
  const durationMonths = getStepDurationMonths(index);
  if (durationMonths == null) return null;
  return `${durationMonths} months`;
}

function getClampedStepYear(year: number, min: number) {
  return Math.min(Math.max(year, min), maxAllowedYear);
}

function updateSelectedStrategy(strategy: RoadmapStrategy) {
  passportStore.setSelectedStrategy({
    id: strategy.id,
    name: strategy.strategyName
  });
  setSelectedGoal(strategy.strategyName);
}

const minYearByIndex = computed(() => {
  const mins: number[] = [];
  let cursor = startYear.value;

  for (let index = 0; index < planYearSteps.value.length; index++) {
    mins[index] = cursor;

    const rawYear = planYearSteps.value[index].year;
    const effectiveYear =
        typeof rawYear === "number" ? Math.max(rawYear, cursor) : cursor;

    cursor = effectiveYear + getStepDurationYears(index);
  }

  return mins;
});

function getStepMinYear(index: number) {
  return minYearByIndex.value[index] ?? startYear.value;
}

function getDisplayedStepYear(index: number, year?: number) {
  return typeof year === "number" ? year : getStepMinYear(index);
}

function yearOptionsForIndex(index: number) {
  const min = getStepMinYear(index);
  return yearOptions.value.filter((year) => year >= min && year <= maxAllowedYear);
}

const yearOptionsByIndex = computed(() =>
    planYearSteps.value.map((_, index) => yearOptionsForIndex(index))
);

function getStepYearOptions(index: number) {
  return yearOptionsByIndex.value[index] ?? [];
}

function stepHasNoValidYearOptions(index: number) {
  return getStepYearOptions(index).length === 0;
}

const firstInvalidStepIndex = computed(() =>
    yearOptionsByIndex.value.findIndex((options) => options.length === 0)
);

function stepLeavesFollowingStepsInvalid(index: number) {
  const invalidIndex = firstInvalidStepIndex.value;
  return invalidIndex > 0 && index === invalidIndex - 1;
}

function clampFollowingYears(fromIndex: number) {
  let cursor =
      getDisplayedStepYear(fromIndex, planYearSteps.value[fromIndex].year) +
      getStepDurationYears(fromIndex);

  for (let index = fromIndex + 1; index < planYearSteps.value.length; index++) {
    const nextYear = getClampedStepYear(
        getDisplayedStepYear(index, planYearSteps.value[index].year),
        cursor
    );

    passportStore.updatePlanStepYear(index, nextYear);
    cursor = nextYear + getStepDurationYears(index);
  }
}

function normalizePlanYears() {
  if (!planYearSteps.value.length) return;

  const firstYear = getDisplayedStepYear(0, planYearSteps.value[0].year);
  passportStore.updatePlanStepYear(0, getClampedStepYear(firstYear, startYear.value));
  clampFollowingYears(0);
}

function setStepYear(index: number, year: number) {
  const min = getStepMinYear(index);
  const nextYear = getClampedStepYear(year, min);
  passportStore.updatePlanStepYear(index, nextYear);
  clampFollowingYears(index);
}

function syncPlanLayout(applyUpdate: () => void) {
  applyUpdate();
  normalizePlanYears();
}

function onMoveMeasure(payload: TimelineMeasureMovePayload) {
  syncPlanLayout(() => {
    passportStore.movePlanMeasure(
        payload.sourceStepIndex,
        payload.sourceMeasureIndex,
        payload.targetStepIndex,
        payload.targetMeasureIndex
    );
  });
}

function onMoveStep(payload: TimelineStepMovePayload) {
  syncPlanLayout(() => {
    passportStore.movePlanStep(
        payload.sourceStepIndex,
        payload.targetStepIndex
    );
  });
}

function onAddStep() {
  syncPlanLayout(() => {
    passportStore.addPlanStep();
  });
}

function onRemoveStep(index: number) {
  syncPlanLayout(() => {
    passportStore.removeTemporaryPlanStep(index);
  });
}

function onYearChange(id: string, year?: number) {
  upgradedYears.value[id] = typeof year === "number" ? year : undefined;
}

const recommendationNamesById = computed(() => new Map<string, string>(
    recommendationGroups.value
        .flatMap((group) => group.recommendations ?? [])
        .map((recommendation) => [recommendation.id, recommendation.name])
));

function getRecommendationNames(recommendationIds: string[] = []) {
  return recommendationIds
      .map((id) => recommendationNamesById.value.get(id))
      .filter((name): name is string => !!name);
}

function toTimelineMeasureViewModel(measure: PlanYearStep["measures"][number]): TimelineMeasureViewModel {
  return {
    key: measure._key,
    name: measure.name,
    description: measure.description,
    ecmKey: measure.ecmKey ?? null,
    routeKey: measure.routeKey ?? null,
    comfort: measure.comfort,
    cost: measure.cost,
    icon: measure.icon,
    defaultIcon: measure.defaultIcon,
    recommendationNames: getRecommendationNames(measure.recommendationIds)
  };
}

function toTimelineStepViewModel(step: PlanYearStep, index: number): TimelineStepViewModel {
  return {
    key: step._key,
    index,
    displayedYear: getDisplayedStepYear(index, step.year),
    yearOptions: getStepYearOptions(index),
    durationLabel: getStepDurationLabel(index),
    leavesFollowingStepsInvalid: stepLeavesFollowingStepsInvalid(index),
    hasNoValidYearOptions: stepHasNoValidYearOptions(index),
    canRemoveEmptyStep: !!step.isTemporary && step.measures.length === 0,
    measures: step.measures.map(toTimelineMeasureViewModel)
  };
}

const timelineSteps = computed<TimelineStepViewModel[]>(() =>
    planYearSteps.value.map((step, index) => toTimelineStepViewModel(step, index))
);

function canMoveTimelineMeasure(payload: TimelineMeasureMovePayload) {
  return canMovePlanMeasure(planYearSteps.value, payload);
}

function canMoveTimelineStep(payload: TimelineStepMovePayload) {
  return canMovePlanStep(planYearSteps.value, payload);
}

const canSubmit = computed(() => {
  return (
      !!selectedStrategyForUi.value &&
      planYearSteps.value.length > 0 &&
      planYearSteps.value.every((step, index) => {
        const displayedYear = getDisplayedStepYear(index, step.year);
        const min = getStepMinYear(index);
        return displayedYear >= min && displayedYear <= maxAllowedYear;
      })
  );
});

function rebuildPlan(strategyId: string) {
  const strategy = strategies.value.find((item) => item.id === strategyId);
  if (!strategy) return;

  updateSelectedStrategy(strategy);
  passportStore.setPlanYearSteps(
      buildPlanFromStrategy(
          strategy,
          recommendationGroups.value as RecommendationGroup[],
          startYear.value,
          maxAllowedYear,
          roadmapConfig.value
      )
  );

  normalizePlanYears();
}

function onStrategyChange(strategyId: string) {
  goalPickerVisible.value = false;
  rebuildPlan(strategyId);
}

async function startPassport() {
  loading.value = true;

  try {
    const roadmapRes = await OcrApiService.getRoadmapMatrix();
    const rawRoadmap = (roadmapRes?.data ?? roadmapRes ?? []) as RoadmapStrategy[];

    roadmap.value = populateRoadmapOrdersWithRecommendationGroups(
        rawRoadmap,
        recommendationGroups.value as RecommendationGroup[]
    );

    const defaultStrategy =
        strategies.value.find((strategy) => strategy.id === defaultStrategyId) ?? strategies.value[0];

    if (!defaultStrategy) return;

    passportStarted.value = true;
    goalPickerVisible.value = false;

    rebuildPlan(defaultStrategy.id);
  } finally {
    loading.value = false;
  }
}

async function submitPassport() {
  try {
    await passportStore.finalizePassport();
  } catch (error) {
    console.error("Failed to generate BRP report", error);
  }
}

async function downloadPassport() {
  try {
    passportStore.downloadGeneratedPdf();
  } catch (error) {
    console.error("Failed to download BRP report", error);
  }
}

function showPopup() {
  confirm.require({
    message: "This will reset your home upgrade roadmap and clear all selected steps. Continue?",
    header: "Reset home upgrade roadmap",
    icon: "pi pi-exclamation-triangle",
    accept: () => {
      clear();
      passportStarted.value = false;
      goalPickerVisible.value = false;
      passportStore.setPlanYearSteps([]);
      passportStore.setSelectedStrategy(undefined);
    }
  });
}

watch(
    alreadyUpgraded,
    (list) => {
      const nextIds = new Set(list.map((recommendation) => recommendation.id));

      list.forEach((recommendation) => {
        if (!(recommendation.id in upgradedYears.value)) {
          upgradedYears.value[recommendation.id] = undefined;
        }
      });

      Object.keys(upgradedYears.value).forEach((id) => {
        if (!nextIds.has(id)) {
          delete upgradedYears.value[id];
        }
      });
    },
    { immediate: true }
);

onMounted(() => {
  if (
      formComplete.value ||
      selectedGoal.value?.trim() ||
      selectedStrategy.value?.id?.trim() ||
      planYearSteps.value.length
  ) {
    passportStarted.value = true;
    normalizePlanYears();
  }
});
</script>

<style scoped lang="scss">
.form {
  background-color: $background-color-default;

  .intro {
    padding: 1rem;
    padding-top: 0;
    margin-bottom: 1.5rem;

    .text {
      color: $font-color-secondary;
    }

    .title-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      margin-bottom: 0.25rem;

      &:deep(.p-button) {
        font-size: 1.2rem;
        padding: 1rem 3rem;
      }

      .header-download-link {
        font-size: 0.95rem;
        padding: 0.25rem 0.5rem;
      }
    }
  }

  .start-actions {
    margin-top: 1.25rem;
    display: flex;
    justify-content: center;
    align-items: center;

    .submit-button {
      font-size: 1.25rem;
      padding: 1.2rem 3rem;
    }
  }

  .passport-copy {
    padding: 0 1rem;
    margin-bottom: 1.5rem;
    color: $font-color-default;
  }

  .actions {
    padding: 1rem;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    gap: 1rem;
    align-items: flex-end;

    .submit-button {
      font-size: 1.25rem;
      padding: 1.2rem 3.5rem;
    }
  }

  .generate-button,
  .submit-button {
    font-size: 1.05rem;
  }

  &:deep(.p-button) {
    &.p-button-outlined {
      border-color: $border-color;
      background-color: $primary-color-shadow;
    }

    &:not(.p-button-outlined):not(.p-button-text) {
      background-color: $secondary-color;
      border-color: $secondary-color;
      color: $font-color-contrast;

      &:hover {
        background-color: $secondary-color-hover;
        border-color: $secondary-color-hover;
      }
    }
  }
}
</style>



